import { Amount } from 'uom';
import { Units } from 'uom-units';
import { formatAmountObject } from '../uom/uom-values';
import { amountAs, makeAmountObject, makeAmountWith } from './amount.helpers';
import { AmountObject, ReduceOptionInterface } from './amount.model';
import { ZERO_SF } from './units-definition';

/**
 * Adds a to b
 * @param a Amount
 * @param b Amount
 */
export const addition = (a, b): Amount.Amount<any> => Amount.plus(a, b);

/**
 * Subtract b from a
 * @param a Amount
 * @param b Amount
 */
export const subtract = (a, b): Amount.Amount<any> => Amount.minus(a, b);

const reduceWith = (items: AmountObject[], { operation, initialValue }: ReduceOptionInterface) =>
  items.map(amountData => makeAmountWith(amountData)).reduce((acc, curr) => operation(acc, curr), initialValue);

/**
 * Reduces a list of `AmountObject` using specific `operation` (`addition`, `Subtract`)
 * @param items `AmountObject[]`
 * @param options `ReduceOptionInterface`
 */
export const reduceAmountWith = (
  items: any[],
  { operation, initialValue, dataset, defaultUom }: ReduceOptionInterface
) =>
  dataset
    ? reduceWith(
        items.map(item => makeAmountObject(item, dataset, defaultUom)),
        { operation, initialValue }
      )
    : reduceWith(items, { operation, initialValue });

/**
 * Calculates the average value from list of values with `{value: number, uom: string }` and returns
 * the average. if any value is invalid 0 is passed. optional param is Unit which will return the value
 * in its equivalence
 *
 * @returns `Amount`
 */
export const average = (values: Array<{ value: number; uom: string }>) => {
  const length = values.length;
  const sum = values
    .map(({ value, uom }) => makeAmountWith({ value: value || 0, uom }))
    .reduce((acc, curr) => addition(acc, curr), ZERO_SF);
  return Amount.divide(sum, length || 1);
};

/**
 * Calculates the average of a list of object using a dataset object to read values & respective UOMs to
 * create Amounts and calculate the average
 * @param items
 * @param options
 *   `value` path to read value from object
 *    `uom` path to read uom from object
 *    `defaultUom` path to read uom from object
 */
export const averageOfDataset = (
  items: any[],
  { defaultUom, skipZeros = false, ...dataset }: { value: string; uom: string; defaultUom: string; skipZeros: boolean }
) => {
  const values = items
    .map(item => makeAmountObject(item, dataset, defaultUom))
    .filter(({ value }) => (skipZeros ? !!value : true));
  return average(values);
};

export const calcPercentage = (dividend, divisor) => (dividend / divisor) * 100;

/**
 * Calculates percentage of between two `Amount` objects
 */
export const getPercentageWithAmount = (dividend, divisor) =>
  divisor != null && divisor.value !== 0
    ? calcPercentage(amountAs(Units.SquareFeet, dividend), amountAs(Units.SquareFeet, divisor))
    : 0;

/**
 * Returns a formatted value of `<number> SF` from a list of report items
 * @param formatOptions Options to format data
 * and to read values and uom form a list of elements
 * @param recordList { data, associatedProperty }[]
 * @returns `<number> SF`
 */
export const formattedAverageWithUom = (
  {
    dataset,
    defaultUom = 'SF',
    locale = 'en-US',
    digits = '.0-0',
    skipZeros = false,
  }: { dataset?: any; defaultUom?: string; locale?: string; digits?: string; skipZeros?: boolean },
  recordList
) => {
  const avgAmount = averageOfDataset(recordList, { ...dataset, defaultUom, skipZeros });
  return formatAmountObject(avgAmount, locale, digits);
};

// Selector that prefers the first valid candidate, otherwise the second;
export const pickPreferredPrice = (value1: any, value2: any): any => {
  const hasValue1 = isNumber(value1) && value1 > 0;
  const hasValue2 = isNumber(value2) && value2 > 0;
  if (hasValue1) return value1;
  if (hasValue2) return value2;
  return null;
};

export const isNumber = (value: any): value is number => {
  return typeof value === 'number' && !isNaN(value as any);
};
